import {
  type ChangeEventHandler,
  type FocusEventHandler,
  type ForwardedRef,
  forwardRef,
  type KeyboardEventHandler,
  type ReactEventHandler,
  useMemo,
  useState,
} from "react";

import Fuse from "fuse.js";
import { get } from "lodash";
import type {
  FieldValues,
  Path,
  PathValue,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from "react-hook-form";

import { classNames, Input, type InputProps, Text } from "@alpacahq/ui/old";

type Fuzzable<T> = { original: T; text: string; value: unknown };

interface FuzzyFormHook<TFormValues extends FieldValues> {
  setValue: UseFormSetValue<TFormValues>;
  trigger: UseFormTrigger<TFormValues>;
  watch: UseFormWatch<TFormValues>;
}

interface FuzzyProps<T, TFormValues extends FieldValues = FieldValues> {
  name: Path<TFormValues>;
  items: (T & object)[];
  strict?: boolean;
  maxResults?: number;
  options?: Fuse.IFuseOptions<unknown>;
  onDidSelect?: (item: T) => void;
  // Pick only the form methods Fuzzy uses so callers with resolver-branded
  // UseFormReturn<T, TContext, TTransformed> still type-check.
  hook: FuzzyFormHook<TFormValues>;
  accessors: {
    text: keyof T & string;
    value: keyof T & string;
    search: (keyof T & string)[];
  };
  value?: string | number | readonly string[];
  onChange?: ChangeEventHandler<HTMLInputElement>;
  className?: string;
  placeholder?: string;
  autoComplete?: InputProps["autoComplete"];
  status?: InputProps["status"];
  errorMsg?: InputProps["errorMsg"];
  label?: InputProps["label"];
  id?: InputProps["id"];
  msgPadded?: InputProps["msgPadded"];
}

const FuzzyInner = <T, TFormValues extends FieldValues = FieldValues>(
  {
    items,
    hook,
    strict,
    options,
    accessors,
    maxResults,
    onDidSelect,
    value,
    name,
    onChange,
    className,
    placeholder,
    id,
    label,
    status,
    errorMsg,
    msgPadded,
    autoComplete = "off",
  }: FuzzyProps<T, TFormValues>,
  // Forwarded so react-hook-form's Controller `field.ref` reaches the real
  // <input>. Without it, RHF's focus-on-error path holds a non-DOM ref and
  // throws "e.focus is not a function" when a Fuzzy field fails validation.
  ref: ForwardedRef<HTMLInputElement>,
) => {
  const [hide, setHide] = useState<boolean>(true);
  const [cursor, setCursor] = useState<number>(0);

  const fuzzable = items.map<Fuzzable<T>>((item) => ({
    original: item,
    text: String(item[accessors.text]),
    value: get(item, accessors.value),
  }));

  const fuse = new Fuse(fuzzable, {
    threshold: 0,
    findAllMatches: true,
    ignoreLocation: true,
    keys: accessors.search.map((key) => "original.".concat(key)),
    ...options,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: cursor is listed to refresh the match list as the highlighted index moves, which also picks up a newly-rebuilt fuse. fuse is `new Fuse(...)` on every render, so listing it would re-run the search on every render. Memoizing fuse is the real fix and is deliberately left for a follow-up.
  const matched = useMemo(
    () => fuse.search(String(value)).slice(0, maxResults || 10),
    [value, cursor],
  );

  // do nothing if value matches zero index text
  const onFocus: FocusEventHandler<HTMLInputElement> = ({ currentTarget }) =>
    setHide(matched.at(0)?.item.value === currentTarget.value);

  const onBlur: FocusEventHandler<HTMLInputElement> = (event) => {
    // if value does not exist as text
    if (
      strict &&
      !fuzzable.find(
        ({ text, value }) =>
          text === event.currentTarget.value ||
          value === event.currentTarget.value,
      )
    ) {
      // wipe value
      hook.setValue(name, "" as PathValue<TFormValues, Path<TFormValues>>);
    }

    // trigger validation
    hook.trigger(name);

    // don't show matches on blur
    setHide(true);
  };

  const handleOnChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    // reset cursor
    setCursor(0);

    // show matched if more exist
    if (matched.length > 0) {
      setHide(false);
    }

    onChange?.(event);
  };

  const select: ReactEventHandler = () => {
    // out of bounds, do nothing
    if (cursor >= matched.length) {
      return;
    }

    const item = matched[cursor]?.item;

    if (!item) {
      return;
    }

    hook.setValue(
      name,
      item.value as PathValue<TFormValues, Path<TFormValues>>,
    );

    onDidSelect?.(item.original);
    setHide(true);
  };

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    switch (event.key) {
      case "Enter":
        select(event);
        event.preventDefault();
        break;
      case "ArrowUp":
        setCursor(Math.max(0, cursor - 1));
        break;
      case "ArrowDown":
        setCursor(Math.min(matched.length - 1, cursor + 1));
        break;
    }

    // trigger validation
    hook.trigger(name);
  };

  return (
    <div className="relative">
      <Input
        ref={ref}
        id={id}
        value={hook.watch(name)}
        onBlur={onBlur}
        onFocus={onFocus}
        onChange={handleOnChange}
        onKeyDown={onKeyDown}
        autoComplete={autoComplete}
        className={className}
        placeholder={placeholder}
        label={label}
        status={status}
        errorMsg={errorMsg}
        msgPadded={msgPadded}
      />
      {!hide && !!hook.watch(name)?.length && (
        <div className="absolute z-50 mt-2.5 w-full overflow-hidden rounded bg-gray-100">
          {matched.length > 0 ? (
            matched
              .map(({ item }) => item)
              .map((item, index) => (
                <div
                  key={item.text}
                  className={classNames(
                    "flex cursor-pointer justify-between px-4 py-2",
                    cursor === index ? "bg-gray-100" : "bg-transparent",
                  )}
                  onMouseDown={(event) => select(event)}
                  onMouseOver={() => setCursor(index)}
                >
                  <Text className="mb-0 text-sm">{item.text}</Text>
                </div>
              ))
          ) : (
            <div className="flex cursor-pointer justify-between px-4 py-2">
              <Text className="mb-0">No results</Text>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// `forwardRef` drops the generic parameter, so re-assert the generic call
// signature to keep `accessors`/`items` inference at each call site.
export const Fuzzy = forwardRef(FuzzyInner) as unknown as <
  T,
  TFormValues extends FieldValues = FieldValues,
>(
  props: FuzzyProps<T, TFormValues> & { ref?: ForwardedRef<HTMLInputElement> },
) => ReturnType<typeof FuzzyInner<T, TFormValues>>;

export default Fuzzy;
